<script setup lang="ts">
import FrameTitleDirectory from "@/components/frames/FrameTitleDirectory.vue";
import ProductCard from "@/components/cards/ProductCard.vue";
import ATitle from "@/components/io/atomic/atoms/fragments/content/ATitle.vue";
import AContent from "@/components/io/atomic/atoms/fragments/content/AContent.vue";
import ListboxDropdown from "@/components/ListboxDropdown.vue";
import Checkbox from 'primevue/checkbox';
import Spinner from "@/components/Spinner.vue";

useSeoMeta({
  title: 'Cleaning & Hygiene Products in Zanzibar | Altracare',
  description: 'Browse ethanol blends, essential chemicals and manufactured hygiene products for Zanzibar hospitality teams. Filter by category to equip every space with compliant solutions.',
  ogTitle: 'Cleaning & Hygiene Products in Zanzibar | Altracare',
  ogDescription: 'Browse ethanol blends, essential chemicals and manufactured hygiene products for Zanzibar hospitality teams. Filter by category to equip every space with compliant solutions.',
  ogType: 'website',
  twitterCard: 'summary_large_image'
})

const { hero } = useHero()
const productsStore = useProductsStore()
const route = useRoute()

// Use global products initialization
const { waitForInitialization } = useGlobalProductsInit()

// Loading state - will be true until global init is complete
const isLoading = ref(true)
const loadingMessage = ref('Loading products...')

// Selected category (single selection)
const selectedCategory = ref<string | null>(null)

// Category options from store
const categoryOptions = computed(() => {
  const options = productsStore.getCategoryOptions || []
  // Add "All Products" option at the beginning
  return [
    { label: 'All Products', value: null, level: 0 },
    ...options
  ]
})

// Initialize the page
onMounted(async () => {
  hero({
    media: {
      type: 'image',
      media: {
        src: '/images/products-header.jpg',
        alt: 'Food Products'
      }
    },
    content: {
      model: 'page',
      content: {
        hairline: '',
        title: 'Our Products',
        description: 'Discover our comprehensive range of high-quality ethanol blends, essential chemicals, and manufactured products designed to meet your specific industry needs.'
      }
    },
    overlays: {
      transition: { mode: 'out-in' },
      overlays: [{
          type: 'gradient',
          styles: {
            kind: 'linear',
            direction: 180, // top -> bottom
            stops: [
              { color: 'rgba(0,0,0,0.00)', at: '45%' },
              { color: 'rgba(0,0,0,0.75)', at: '80%' },
              { color: 'rgba(0,0,0,1.00)',  at: '100%' }
            ],
            blendMode: 'normal'
          },
          breakpoints: [
            {
              from: 1024, until: -1,
              styles: {
                stops: [
                  { color: 'rgba(0,0,0,0.00)', at: '40%' },
                  { color: 'rgba(0,0,0,0.85)', at: '78%' },
                  { color: 'rgba(0,0,0,1.00)',  at: '100%' }
                ]
              }
            }
          ]
        },{
          type: 'gradient',
          styles: {
            kind: 'linear',
            direction: 'to right',
            stops: [
              { color: 'rgba(0,40,140,0.45)', at: '0%'  },
              { color: 'rgba(0,40,140,0.20)', at: '35%' },
              { color: 'rgba(0,40,140,0.00)', at: '70%' }
            ],
            blendMode: 'soft-light'
          },
          breakpoints: [
            {
              from: 1024, until: -1,
              styles: {
                direction: 'to bottom right',
                stops: [
                  { color: 'rgba(0,40,140,0.50)', at: '0%'  },
                  { color: 'rgba(0,40,140,0.00)', at: '60%' }
                ],
                blendMode: 'multiply'
              }
            }
          ]
        }]
    },
  })

  // Wait for global initialization to complete
  const initialized = await waitForInitialization()
  
  if (initialized) {
    console.log('[Products Page] Using globally initialized products')
    console.log(`[Products Page] Total products available: ${productsStore.productList.length}`)
    
    // Check for category in query params
    setDefaultSelectedCategory()
  } else {
    console.error('[Products Page] Failed to initialize products')
  }
  
  isLoading.value = false
})

// Filter state
const filterNatured = ref(false)
const filterUnNatured = ref(false)
const filterOriginNatural = ref(false)
const filterOriginSynthetic = ref(false)

// Get selected category label for display
const selectedCategoryLabel = computed(() => {
  if (!selectedCategory.value) return 'All Products'
  const option = categoryOptions.value.find(opt => opt.value === selectedCategory.value)
  return option ? option.label : 'All Products'
})

// Build dropdown options with hierarchy indicators
const dropdownOptions = computed(() => {
  return categoryOptions.value.map(option => ({
    ...option,
    label: option.level > 0 ? `${'— '.repeat(option.level)}${option.label}` : option.label
  }))
})

// Adapter to transform lightweight product to ProductCard format
const transformProductForCard = (product: any) => ({
  titles: {
    card: product.title,
    admin: product.title,
    short: product.title,
    long: product.title,
    page: product.title,
    introHairline: ''
  },
  description: {
    short: product.shortDescription,
    full: product.shortDescription
  },
  category: product.category,
  categoryName: product.categoryName, // Add the actual category name
  categories: product.categories, // Include categories array
  image: product.image,
  slug: product.slug,
  images: product.image ? [product.image] : [],
  origin: product.origin,
  denaturation: product.denaturation
})

// Filter products based on all criteria
const filteredProducts = computed(() => {
  // Start with either all products or filtered by category
  let filtered = selectedCategory.value 
    ? productsStore.productList.filter(product => {
        // Check if selected category matches the primary category
        if (product.category === selectedCategory.value) return true
        
        // Check if selected category exists in the categories array
        if (product.categories && product.categories.length > 0) {
          return product.categories.some(cat => cat.slug === selectedCategory.value)
        }
        
        return false
      })
    : productsStore.productList
  
  // Apply origin filters
  if (filterOriginNatural.value || filterOriginSynthetic.value) {
    filtered = filtered.filter(product => {
      if (!product.origin) return false
      if (filterOriginNatural.value && filterOriginSynthetic.value) return true
      if (filterOriginNatural.value) return product.origin === 'natural'
      if (filterOriginSynthetic.value) return product.origin === 'synthetic'
      return false
    })
  }
  
  // Apply denaturation filters
  if (filterNatured.value || filterUnNatured.value) {
    filtered = filtered.filter(product => {
      if (!product.denaturation) return false
      if (filterNatured.value && filterUnNatured.value) return true
      if (filterNatured.value) return product.denaturation === 'denatured'
      if (filterUnNatured.value) return product.denaturation === 'undenatured'
      return false
    })
  }
  
  // Transform products for ProductCard component
  return filtered.map(transformProductForCard)
})

// Check query params for default category
function setDefaultSelectedCategory() {
  const validCategories = productsStore.flatCategories.map(cat => cat.slug)
  
  for (const param of validCategories) {
    if (route.query[param] !== undefined) {
      selectedCategory.value = param
      break
    }
  }
}

// Watch for route changes
watch(() => route.query, () => {
  setDefaultSelectedCategory()
})

// No need to watch category changes anymore - filtering happens in computed property
</script>

<template>
<Frame class="page-products gpt-3">
  <FrameTitleDirectory id="secproducts" class="gmb-4" dir="ltr">
      <template #title>
        <Frame class="sticky-container sticky top-32" w="grid g-1">
          <Frame class="sec-features-title bg-white gpy-1" w="grid g-1">
            <!-- <ATitle class="sticky-title" hairline="360 degree solutions" primary="Our Products"
              subtitle="" /> -->

              <div class="a-title-hairline text-gray-400">
                360 degree solutions
              </div>
              <div class="a-title-primary">
                Our Products
              </div>

            <Frame>
              <div class="sec-products-select-filter">
                <ListboxDropdown
                  v-if="!isLoading"
                  v-model="selectedCategory"
                  :options="dropdownOptions"
                  :label="selectedCategoryLabel"
                  placeholder="Select Category"
                  class="w-full"
                />
                <div v-else class="loading-placeholder">
                  Loading categories...
                </div>
              </div>
            </Frame>
          </Frame>
         
        </Frame>
      </template>
      <template #directory>
        <div v-if="isLoading" class="loading-state gpy-4 text-center">
          <Spinner />
          <p class="gmt-1 text-sm text-gray-600">{{ loadingMessage }}</p>
        </div>
        
        <template v-else>
          <Wrap class="w-full grid grid-cols-[1fr] ts:gc-300px g-0 gmb-1">
            <div class="sec-results">
              <div class="sec-results-count"> 
                <span>{{ filteredProducts.length }}</span> products
                <span v-if="!productsStore.allProductsLoaded" class="text-xs text-gray-500 ml-2">
                  (loading more...)
                </span>
              </div>
            </div>
          </Wrap>

          <Wrap class="w-full grid grid-cols-[1fr] ts:gc-300px g-0">
            <template v-for="product in filteredProducts" :key="product.id">
              <ProductCard :item="product" />
            </template>
          </Wrap>
        </template>
      </template>
    </FrameTitleDirectory>
</Frame>
</template>

<style lang="scss">
.io-hero {
 &.products {
  .sec-titles {
    .sec-title {
      @include mq($until: ds) {
        font-size: fs(7) !important;
        line-height: gr(2.5)! important;
      }
    }
  }
 }
}
</style>

<style lang="scss" scoped>

.sec-product-filters {

  .sec-divider {
    .line {
      height: 1px;
      width: 70px;
      background: var(--base-400);
    }
  }
  .products-filter {
    .filter-label {
      cursor: pointer;
      display:flex;
      align-items: center;
      gap: gr(0.25);
      span {
        font-size: fs(-0.5);
      }    
    }
  }
}

.sec-results-count {
  font-size: fs(-1);
  color: var(--base-600);
  text-transform: uppercase;
  span {
    color: var(--base-600);
  }
}

.loading-placeholder {
  padding: gr(0.5) gr(1);
  background-color: var(--base-100);
  border: 1px solid var(--base-300);
  border-radius: 4px;
  color: var(--base-500);
  font-size: fs(-1);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

</style>
